import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const ContactContainer = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.cream};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, 
      ${props => props.theme.colors.overlay} 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const ContactWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  position: relative;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.title};
  color: ${props => props.theme.colors.black};
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: ${props => props.theme.spacing.xl};
  opacity: 0;
  transform: translateY(30px);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.md};
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.wood},
      transparent
    );
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxl};
  padding: ${props => props.theme.spacing.lg};
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  background: ${props => props.theme.colors.cream};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.wood};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
`;

const ContactTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.black};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 1.5rem;
  transition: color 0.3s ease;
  position: relative;
`;

const ContactCard = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.medium};
  text-align: center;
  transform: translateY(30px);
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: ${props => props.theme.shadows.large};
    background: linear-gradient(
      135deg,
      ${props => props.theme.colors.white} 0%,
      rgba(255, 255, 255, 0.95) 100%
    );

    ${IconWrapper} {
      transform: scale(1.1) rotate(5deg);
      background: ${props => props.theme.colors.wood};
      color: ${props => props.theme.colors.white};
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    }

    ${ContactTitle} {
      color: ${props => props.theme.colors.wood};
    }

    &::before {
      transform: translateX(0);
    }

    &::after {
      transform: translateY(0);
    }
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: ${props => props.theme.colors.wood};
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: ${props => props.theme.borderRadius.large};
  }

  &::before {
    top: -2px;
    left: -2px;
    width: calc(100% + 4px);
    height: 4px;
    transform: translateX(-100%);
    border-top-left-radius: ${props => props.theme.borderRadius.large};
    border-top-right-radius: ${props => props.theme.borderRadius.large};
  }

  &::after {
    top: -2px;
    right: -2px;
    width: 4px;
    height: calc(100% + 4px);
    transform: translateY(-100%);
    border-top-right-radius: ${props => props.theme.borderRadius.large};
    border-bottom-right-radius: ${props => props.theme.borderRadius.large};
  }
`;

const ContactText = styled.p`
  color: ${props => props.theme.colors.black};
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: 1.1rem;
  opacity: 0.9;

  a {
    color: inherit;
    text-decoration: none;
    transition: all 0.3s ease;
    position: relative;
    padding-bottom: 2px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background: ${props => props.theme.colors.wood};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease;
    }

    &:hover {
      color: ${props => props.theme.colors.wood};
      
      &::after {
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }
`;

const SocialSection = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  opacity: 0;
  transform: translateY(30px);
`;

const SocialTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.black};
  margin-bottom: ${props => props.theme.spacing.xl};
  font-size: 2.2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.wood},
      transparent
    );
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
`;

const SocialLink = styled.a`
  color: ${props => props.theme.colors.wood};
  font-size: 2.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(
      45deg,
      ${props => props.theme.colors.wood},
      ${props => props.theme.colors.woodLight}
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    color: ${props => props.theme.colors.woodDark};

    &::before {
      opacity: 1;
    }
  }
`;

const contactInfo = [
  {
    icon: <FaEnvelope />,
    title: 'Email',
    content: <a href="mailto:michelsanz@hotmail.fr">michelsanz@hotmail.fr</a>
  },
  {
    icon: <FaPhone />,
    title: 'Téléphone',
    content: <a href="tel:+33600000000">+33 6 07 90 65 27</a>
  },
  {
    icon: <FaMapMarkerAlt />,
    title: 'Localisation',
    content: 'Montpellier, France'
  }
];

const socialLinks = [
  {
    icon: <FaFacebook />,
    url: 'https://www.facebook.com/annasanz34/',
    label: 'Facebook'
  },
];

function Contact() {
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const socialRef = useRef(null);

  useEffect(() => {
    // Animation du titre
    gsap.to(titleRef.current, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out'
    });

    // Animation des cartes
    cardsRef.current.forEach((card, index) => {
      gsap.to(card, {
        scrollTrigger: {
          trigger: card,
          start: 'top bottom-=100',
          toggleActions: 'play none none reverse'
        },
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: index * 0.2,
        ease: 'power2.out'
      });
    });

    // Animation de la section sociale
    gsap.to(socialRef.current, {
      scrollTrigger: {
        trigger: socialRef.current,
        start: 'top bottom-=100',
        toggleActions: 'play none none reverse'
      },
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: 0.5,
      ease: 'power2.out'
    });
  }, []);

  return (
    <ContactContainer>
      <ContactWrapper>
        <PageTitle ref={titleRef}>Contact</PageTitle>
        
        <ContactGrid>
          {contactInfo.map((info, index) => (
            <ContactCard 
              key={index}
              ref={el => cardsRef.current[index] = el}
            >
              <IconWrapper>{info.icon}</IconWrapper>
              <ContactTitle>{info.title}</ContactTitle>
              <ContactText>{info.content}</ContactText>
            </ContactCard>
          ))}
        </ContactGrid>

        <SocialSection ref={socialRef}>
          <SocialTitle>Suivez-nous</SocialTitle>
          <SocialLinks>
            {socialLinks.map((social, index) => (
              <SocialLink 
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
              >
                {social.icon}
              </SocialLink>
            ))}
          </SocialLinks>
        </SocialSection>
      </ContactWrapper>
    </ContactContainer>
  );
}

export default Contact;
